<template>
  <div class="user-management">
    <!-- 页面标题和操作栏 -->
    <el-card class="header-card" shadow="hover">
      <div class="header-content">
        <div class="title-section">
          <h2>
            <i class="el-icon-user-solid"></i>
            用户管理
          </h2>
          <p>管理系统用户，包括新增、编辑、删除用户等操作</p>
        </div>
        <div class="action-section">
          <el-button type="primary" icon="el-icon-plus" @click="showAddDialog">
            新增用户
          </el-button>
          <el-button icon="el-icon-refresh" @click="loadUserList">
            刷新
          </el-button>
        </div>
      </div>
    </el-card>

    <!-- 搜索和筛选 -->
    <el-card class="search-card" shadow="hover">
      <el-row :gutter="20">
        <el-col :span="8">
          <el-input
            v-model="searchForm.keyword"
            placeholder="请输入用户名或昵称"
            prefix-icon="el-icon-search"
            clearable
            @keyup.enter.native="handleSearch"
          />
        </el-col>
        <el-col :span="6">
          <el-select v-model="searchForm.role" placeholder="选择角色" clearable>
            <el-option label="编辑员" value="editor" />
            <el-option label="普通用户" value="viewer" />
          </el-select>
        </el-col>
        <el-col :span="6">
          <el-button type="primary" icon="el-icon-search" @click="handleSearch">
            搜索
          </el-button>
          <el-button icon="el-icon-refresh-left" @click="resetSearch">
            重置
          </el-button>
        </el-col>
      </el-row>
    </el-card>

    <!-- 用户列表 -->
    <el-card class="table-card" shadow="hover">
      <!-- 桌面端表格 -->
      <el-table
        :data="userList"
        v-loading="loading"
        stripe
        border
        style="width: 100%"
        class="desktop-table"
      >
        <el-table-column prop="userId" label="用户ID" width="180" />
        <el-table-column prop="username" label="用户名" width="220" />
        <el-table-column prop="nickname" label="昵称" width="220" />
        <el-table-column prop="role" label="角色" width="150">
          <template slot-scope="scope">
            <el-tag
              :type="getRoleTagType(scope.row.role)"
              size="small"
            >
              {{ getRoleText(scope.row.role) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="createdTime" label="创建时间" width="240">
          <template slot-scope="scope">
            {{ formatTime(scope.row.createdTime) }}
          </template>
        </el-table-column>
        <el-table-column label="操作" min-width="300">
          <template slot-scope="scope">
            <el-button
              type="primary"
              size="mini"
              icon="el-icon-edit"
              @click="showEditDialog(scope.row)"
            >
              编辑
            </el-button>
            <el-button
              v-if="scope.row.role === 'viewer' || scope.row.role === 'editor'"
              type="warning"
              size="mini"
              icon="el-icon-key"
              @click="showPermissionDialog(scope.row)"
            >
              权限
            </el-button>
            <el-button
              type="info"
              size="mini"
              icon="el-icon-document"
              @click="showLoginLogs(scope.row)"
            >
              记录
            </el-button>
            <el-button
              type="danger"
              size="mini"
              icon="el-icon-delete"
              @click="handleDelete(scope.row)"
              :disabled="scope.row.role === 'admin'"
            >
              删除
            </el-button>
          </template>
        </el-table-column>
      </el-table>
      
      <!-- 移动端卡片列表 -->
      <div class="mobile-card-list" v-loading="loading">
        <div 
          v-for="user in userList" 
          :key="user.userId"
          class="user-card"
        >
          <div class="card-header">
            <div class="user-info">
              <el-avatar :size="40" icon="el-icon-user-solid"></el-avatar>
              <div class="user-details">
                <h3 class="user-name">{{ user.username }}</h3>
                <p class="user-nickname" v-if="user.nickname">{{ user.nickname }}</p>
              </div>
            </div>
            <el-tag
              :type="getRoleTagType(user.role)"
              size="small"
            >
              {{ getRoleText(user.role) }}
            </el-tag>
          </div>
          <div class="card-meta">
            <div class="meta-item">
              <span class="meta-label">用户ID:</span>
              <span class="meta-value">{{ user.userId }}</span>
            </div>
            <div class="meta-item">
              <span class="meta-label">创建时间:</span>
              <span class="meta-value">{{ formatTime(user.createdTime) }}</span>
            </div>
          </div>
          <div class="card-actions">
            <el-button
              type="primary"
              size="mini"
              icon="el-icon-edit"
              @click="showEditDialog(user)"
            >
              编辑
            </el-button>
            <el-button
              v-if="user.role === 'viewer' || user.role === 'editor'"
              type="warning"
              size="mini"
              icon="el-icon-key"
              @click="showPermissionDialog(user)"
            >
              权限
            </el-button>
            <el-button
              type="info"
              size="mini"
              icon="el-icon-document"
              @click="showLoginLogs(user)"
            >
              记录
            </el-button>
            <el-button
              type="danger"
              size="mini"
              icon="el-icon-delete"
              @click="handleDelete(user)"
              :disabled="user.role === 'admin'"
            >
              删除
            </el-button>
          </div>
        </div>
      </div>

      <!-- 分页 -->
      <div class="pagination-container">
        <el-pagination
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :current-page="pagination.page"
          :page-sizes="[10, 20, 50, 100]"
          :page-size="pagination.size"
          layout="total, sizes, prev, pager, next, jumper"
          :total="pagination.total"
        />
      </div>
    </el-card>

    <!-- 新增/编辑用户对话框 -->
    <el-dialog
      :title="dialogTitle"
      :visible.sync="dialogVisible"
      width="500px"
      :close-on-click-modal="false"
    >
      <el-form
        ref="userForm"
        :model="userForm"
        :rules="userRules"
        label-width="80px"
      >
        <el-form-item label="用户名" prop="username">
          <el-input
            v-model="userForm.username"
            :disabled="isEdit"
            placeholder="请输入用户名"
          />
        </el-form-item>
        <el-form-item label="密码" prop="password" v-if="!isEdit">
          <el-input
            v-model="userForm.password"
            type="password"
            placeholder="请输入密码"
            show-password
          />
        </el-form-item>
        <el-form-item label="新密码" prop="newPassword" v-if="isEdit">
          <el-input
            v-model="userForm.newPassword"
            type="password"
            placeholder="留空则不修改密码"
            show-password
          />
        </el-form-item>
        <el-form-item label="昵称" prop="nickname">
          <el-input
            v-model="userForm.nickname"
            placeholder="请输入昵称"
          />
        </el-form-item>
        <el-form-item label="角色" prop="role">
          <el-select v-model="userForm.role" placeholder="请选择角色" style="width: 100%">
            <el-option label="编辑员" value="editor" />
            <el-option label="普通用户" value="viewer" />
          </el-select>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleSubmit" :loading="submitLoading">
          确定
        </el-button>
      </div>
    </el-dialog>
    
    <!-- 权限设置对话框 -->
    <el-dialog
      title="设置文集访问权限"
      :visible.sync="permissionDialogVisible"
      width="800px"
      :close-on-click-modal="false"
      class="permission-dialog"
    >
      <div class="permission-header">
        <span>用户：<strong>{{ currentUser.username }}</strong></span>
        <el-tag type="info" size="small">{{ getRoleText(currentUser.role) }}</el-tag>
        <span class="selected-count">已选择 {{ selectedCollections.length }} 个文集</span>
      </div>
      
      <div class="permission-content">
        <p class="permission-tip">
          <i class="el-icon-info"></i>
          请选择该用户可以访问的文集（admin可以访问所有文集）
        </p>
        
        <!-- 搜索框 -->
        <div class="permission-search">
          <el-input
            v-model="permissionSearchKeyword"
            placeholder="搜索文集名称"
            prefix-icon="el-icon-search"
            clearable
            @input="handlePermissionSearch"
          />
        </div>
        
        <!-- 文集列表 -->
        <el-table
          ref="permissionTable"
          :data="displayCollections"
          v-loading="permissionLoading"
          border
          stripe
          height="400"
          @selection-change="handleSelectionChange"
          :row-key="getRowKey"
        >
          <el-table-column
            type="selection"
            width="55"
            :reserve-selection="true"
          />
          <el-table-column prop="collectionId" label="文集ID" width="80" />
          <el-table-column prop="collectionName" label="文集名称" />
          <el-table-column prop="permission" label="访问权限" width="100">
            <template slot-scope="scope">
              <el-tag :type="scope.row.permission === 'public' ? 'success' : 'warning'" size="mini">
                {{ scope.row.permission === 'public' ? '公开' : '私有' }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="creatorName" label="创建者" width="120" />
        </el-table>
        
        <!-- 分页 -->
        <div class="permission-pagination">
          <el-pagination
            @size-change="handlePermissionSizeChange"
            @current-change="handlePermissionPageChange"
            :current-page="permissionPagination.page"
            :page-sizes="[10, 20, 50, 100]"
            :page-size="permissionPagination.size"
            layout="total, sizes, prev, pager, next"
            :total="permissionPagination.total"
          />
        </div>
      </div>
      
      <div slot="footer" class="dialog-footer">
        <el-button @click="permissionDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleSavePermission" :loading="permissionSaving">
          保存（已选 {{ selectedCollections.length }} 个）
        </el-button>
      </div>
    </el-dialog>

    <!-- 用户登录记录弹窗 -->
    <UserLoginLogsDialog ref="loginLogsDialog" />
  </div>
</template>

<script>
import { USER_PATHS, COLLECTION_PATHS } from '@/api/paths'
import UserLoginLogsDialog from '@/components/UserLoginLogsDialog.vue'
import { authUtils } from '@/utils/auth'

export default {
  name: 'UserManagement',
  components: {
    UserLoginLogsDialog
  },
  data() {
    return {
      // 用户列表数据
      userList: [],
      loading: false,
      
      // 搜索表单
      searchForm: {
        keyword: '',
        role: ''
      },
      
      // 分页信息
      pagination: {
        page: 1,
        size: 10,
        total: 0,
        pages: 0
      },
      
      // 对话框相关
      dialogVisible: false,
      dialogTitle: '新增用户',
      isEdit: false,
      submitLoading: false,
      
      // 用户表单
      userForm: {
        userId: null,
        username: '',
        password: '',
        newPassword: '',
        nickname: '',
        role: 'viewer'
      },
      
      // 权限对话框相关
      permissionDialogVisible: false,
      permissionLoading: false,
      permissionSaving: false,
      currentUser: {},
      permissionCollections: [],
      selectedCollections: [],
      permissionSearchKeyword: '',
      permissionFilter: '',
      permissionPagination: {
        page: 1,
        size: 10,
        total: 0
      },
      isInitializingPermission: false, // 标记是否正在初始化权限
      
      // 表单验证规则
      userRules: {
        username: [
          { required: true, message: '请输入用户名', trigger: 'blur' },
          { min: 3, max: 20, message: '用户名长度在 3 到 20 个字符', trigger: 'blur' }
        ],
        password: [
          { required: true, message: '请输入密码', trigger: 'blur' },
          { min: 6, max: 20, message: '密码长度在 6 到 20 个字符', trigger: 'blur' }
        ],
        nickname: [
          { max: 50, message: '昵称长度不能超过 50 个字符', trigger: 'blur' }
        ],
        role: [
          { required: true, message: '请选择角色', trigger: 'change' }
        ]
      }
    }
  },
  
  computed: {
    // 当前页显示的文集列表（直接使用后端返回的数据）
    displayCollections() {
      return this.permissionCollections
    }
  },
  
  mounted() {
    if (!authUtils.isAdmin()) {
      this.$message.warning('无权限访问管理后台，已自动返回文档列表')
      this.$router.replace('/')
      return
    }
    this.loadUserList()
  },
  
  methods: {
    // 加载用户列表
    async loadUserList() {
      this.loading = true
      try {
        const params = {
          page: this.pagination.page,
          size: this.pagination.size
        }
        
        if (this.searchForm.keyword) {
          params.keyword = this.searchForm.keyword
        }
        if (this.searchForm.role) {
          params.role = this.searchForm.role
        }
        
        const response = await this.$get(USER_PATHS.GET_USER_LIST, { params })
        
        if (response.data && response.data.code === 200) {
          const data = response.data.data
          this.userList = data.list || []
          this.pagination.total = data.total || 0
          this.pagination.pages = data.pages || 0
        } else {
          this.$message.error(response.data?.msg || '获取用户列表失败')
        }
      } catch (error) {
        console.error('获取用户列表失败:', error)
        this.$message.error('获取用户列表失败')
      } finally {
        this.loading = false
      }
    },
    
    // 搜索用户
    handleSearch() {
      this.pagination.page = 1
      this.loadUserList()
    },
    
    // 重置搜索
    resetSearch() {
      this.searchForm = {
        keyword: '',
        role: ''
      }
      this.pagination.page = 1
      this.loadUserList()
    },
    
    // 分页大小改变
    handleSizeChange(size) {
      this.pagination.size = size
      this.pagination.page = 1
      this.loadUserList()
    },
    
    // 当前页改变
    handleCurrentChange(page) {
      this.pagination.page = page
      this.loadUserList()
    },
    
    // 显示新增对话框
    showAddDialog() {
      this.dialogTitle = '新增用户'
      this.isEdit = false
      this.dialogVisible = true
      this.resetUserForm()
    },
    
    // 显示编辑对话框
    showEditDialog(user) {
      this.dialogTitle = '编辑用户'
      this.isEdit = true
      this.dialogVisible = true
      this.userForm = {
        userId: user.userId,
        username: user.username,
        password: '',
        newPassword: '',
        nickname: user.nickname || '',
        role: user.role
      }
    },
    
    // 重置用户表单
    resetUserForm() {
      this.userForm = {
        userId: null,
        username: '',
        password: '',
        newPassword: '',
        nickname: '',
        role: 'viewer'
      }
      if (this.$refs.userForm) {
        this.$refs.userForm.resetFields()
      }
    },
    
    // 提交表单
    async handleSubmit() {
      try {
        await this.$refs.userForm.validate()
      } catch (error) {
        return
      }
      
      this.submitLoading = true
      try {
        if (this.isEdit) {
          await this.updateUser()
        } else {
          await this.createUser()
        }
      } finally {
        this.submitLoading = false
      }
    },
    
    // 创建用户
    async createUser() {
      try {
        const params = {
          username: this.userForm.username,
          password: this.userForm.password,
          nickname: this.userForm.nickname,
          role: this.userForm.role
        }
        
        const response = await this.$post(USER_PATHS.CREATE_USER, params)
        
        if (response.data && response.data.code === 200) {
          this.$message.success(response.data.msg || '用户创建成功')
          this.dialogVisible = false
          this.loadUserList()
        } else {
          this.$message.error(response.data?.msg || '用户创建失败')
        }
      } catch (error) {
        console.error('创建用户失败:', error)
        this.$message.error(error.response?.data?.msg || '用户创建失败')
      }
    },
    
    // 更新用户
    async updateUser() {
      try {
        const params = {
          userId: this.userForm.userId,
          nickname: this.userForm.nickname,
          role: this.userForm.role
        }
        
        // 如果提供了新密码，则更新密码
        if (this.userForm.newPassword) {
          params.password = this.userForm.newPassword
        }
        
        const response = await this.$post(USER_PATHS.UPDATE_USER, params)
        
        if (response.data && response.data.code === 200) {
          this.$message.success(response.data.msg || '用户信息更新成功')
          this.dialogVisible = false
          this.loadUserList()
        } else {
          this.$message.error(response.data?.msg || '用户信息更新失败')
        }
      } catch (error) {
        console.error('更新用户失败:', error)
        this.$message.error(error.response?.data?.msg || '用户信息更新失败')
      }
    },
    
    // 删除用户
    handleDelete(user) {
      this.$confirm(`确定要删除用户 "${user.username}" 吗？`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          const response = await this.$post(USER_PATHS.DELETE_USER, {
            userId: user.userId
          })
          
          if (response.data && response.data.code === 200) {
            this.$message.success(response.data.msg || '用户删除成功')
            this.loadUserList()
          } else {
            this.$message.error(response.data?.msg || '用户删除失败')
          }
        } catch (error) {
          console.error('删除用户失败:', error)
          this.$message.error(error.response?.data?.msg || '用户删除失败')
        }
      }).catch(() => {
        this.$message.info('已取消删除')
      })
    },
    
    // 获取角色标签类型
    getRoleTagType(role) {
      const roleTypes = {
        admin: 'danger',
        editor: 'warning',
        viewer: 'success'
      }
      return roleTypes[role] || 'info'
    },
    
    // 获取角色文本
    getRoleText(role) {
      const roleTexts = {
        admin: '管理员',
        editor: '编辑员',
        viewer: '普通用户'
      }
      return roleTexts[role] || role
    },
    
    // 格式化时间
    formatTime(timestamp) {
      if (!timestamp) return '-'
      const date = new Date(timestamp * 1000)
      return date.toLocaleString('zh-CN')
    },
    
    // 显示权限设置对话框
    async showPermissionDialog(user) {
      this.currentUser = user
      this.permissionDialogVisible = true
      this.selectedCollections = []
      this.permissionSearchKeyword = ''
      this.permissionFilter = ''
      this.permissionPagination = {
        page: 1,
        size: 10,
        total: 0
      }
      
      // 从后端查询用户的最新信息，获取已保存的权限
      await this.loadUserPermissions(user.userId)
      
      // 加载第一页文集
      await this.loadPermissionCollections()
    },
    
    // 从后端查询用户权限
    async loadUserPermissions(userId) {
      try {
        const response = await this.$get(USER_PATHS.GET_USER_BY_ID, { 
          params: { userId } 
        })
        if (response.data && response.data.code === 200) {
          const user = response.data.data
          
          if (user && user.accessibleDocuments) {
            // 判断是字符串还是数组
            if (typeof user.accessibleDocuments === 'string') {
              // 如果是字符串，需要解析JSON
              try {
                const parsed = JSON.parse(user.accessibleDocuments)
                this.selectedCollections = Array.isArray(parsed) ? parsed.map(id => parseInt(id)) : []
              } catch (e) {
                this.selectedCollections = []
              }
            } else if (Array.isArray(user.accessibleDocuments)) {
              // 如果已经是数组，直接使用
              this.selectedCollections = user.accessibleDocuments.map(id => parseInt(id))
            } else {
              this.selectedCollections = []
            }
          } else {
            this.selectedCollections = []
          }
        }
      } catch (error) {
        console.error('加载用户权限失败:', error)
        this.selectedCollections = []
      }
    },
    
    // 加载文集列表（分页）
    async loadPermissionCollections() {
      this.permissionLoading = true
      try {
        const params = {
          page: this.permissionPagination.page,
          size: this.permissionPagination.size
        }
        
        if (this.permissionSearchKeyword.trim()) {
          params.keyword = this.permissionSearchKeyword
        }
        
        if (this.permissionFilter) {
          params.permission = this.permissionFilter
        }
        
        const response = await this.$get(COLLECTION_PATHS.GET_COLLECTION_PAGE, { params })
        if (response.data && response.data.code === 200) {
          const data = response.data.data
          this.permissionCollections = data.list || []
          this.permissionPagination.total = data.total || 0
          
          // 设置当前页的选中状态
          this.$nextTick(() => {
            if (this.$refs.permissionTable) {
              // 标记正在初始化，防止触发 handleSelectionChange
              this.isInitializingPermission = true
              
              this.$refs.permissionTable.clearSelection()
              
              // 保存原始的选中列表
              const originalSelected = [...this.selectedCollections]
              
              this.permissionCollections.forEach(collection => {
                const collectionId = parseInt(collection.collectionId)
                const shouldSelect = originalSelected.some(id => parseInt(id) === collectionId)
                
                if (shouldSelect) {
                  this.$refs.permissionTable.toggleRowSelection(collection, true)
                }
              })
              
              // 恢复选中列表（防止被 handleSelectionChange 修改）
              this.selectedCollections = originalSelected
              
              // 延迟解除初始化标记
              setTimeout(() => {
                this.isInitializingPermission = false
              }, 100)
            }
          })
        }
      } catch (error) {
        this.$message.error('加载文集列表失败')
        console.error(error)
      } finally {
        this.permissionLoading = false
      }
    },
    
    // 处理权限搜索
    handlePermissionSearch() {
      // 重置到第一页并重新加载
      this.permissionPagination.page = 1
      this.loadPermissionCollections()
    },
    
    // 处理表格选择变化（支持跨页选择）
    handleSelectionChange(selection) {
      // 如果正在初始化，不处理选择变化
      if (this.isInitializingPermission) {
        return
      }
      
      // 获取当前页所有文集的ID（确保是数字）
      const currentPageIds = this.permissionCollections.map(item => parseInt(item.collectionId))
      
      // 获取当前页被选中的ID（确保是数字）
      const currentPageSelected = selection.map(item => parseInt(item.collectionId))
      
      // 移除当前页所有ID（无论之前是否选中）
      this.selectedCollections = this.selectedCollections.filter(id => !currentPageIds.includes(parseInt(id)))
      
      // 添加当前页新选中的ID
      this.selectedCollections.push(...currentPageSelected)
      
      // 去重并确保都是数字
      this.selectedCollections = [...new Set(this.selectedCollections.map(id => parseInt(id)))]
    },
    
    // 获取行的key
    getRowKey(row) {
      return row.collectionId
    },
    
    // 处理每页大小变化
    handlePermissionSizeChange(size) {
      this.permissionPagination.size = size
      this.permissionPagination.page = 1
      this.loadPermissionCollections()
    },
    
    // 处理页码变化
    handlePermissionPageChange(page) {
      this.permissionPagination.page = page
      this.loadPermissionCollections()
    },
    
    // 保存权限
    async handleSavePermission() {
      this.permissionSaving = true
      try {
        const documentIds = JSON.stringify(this.selectedCollections)
        const response = await this.$post(USER_PATHS.UPDATE_USER_PERMISSIONS, {
          userId: this.currentUser.userId,
          documentIds: documentIds // 虽然参数名是documentIds但传的是collectionId
        })
        
        if (response.data && response.data.code === 200) {
          this.$message.success('权限设置成功')
          this.permissionDialogVisible = false
          this.loadUserList()
        } else {
          this.$message.error(response.data?.msg || '权限设置失败')
        }
      } catch (error) {
        console.error('保存权限失败:', error)
        this.$message.error(error.response?.data?.msg || '权限设置失败')
      } finally {
        this.permissionSaving = false
      }
    },
    
    // 显示用户登录记录
    showLoginLogs(user) {
      this.$refs.loginLogsDialog.open(user.userId, user.username)
    }
  }
}
</script>

<style scoped>
.user-management {
  padding: 0;
}

.header-card {
  margin-bottom: 20px;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.title-section h2 {
  margin: 0 0 8px 0;
  color: #303133;
  font-size: 20px;
  display: flex;
  align-items: center;
}

.title-section h2 i {
  margin-right: 8px;
  color: #409EFF;
}

.title-section p {
  margin: 0;
  color: #909399;
  font-size: 14px;
}

.search-card {
  margin-bottom: 20px;
}

.table-card {
  margin-bottom: 20px;
}

.pagination-container {
  margin-top: 20px;
  text-align: right;
}

.dialog-footer {
  text-align: right;
}

/* 移动端卡片样式 */
.mobile-card-list {
  display: none;
}

.user-card {
  background: #fff;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid #f0f2f5;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
}

.user-details {
  flex: 1;
}

.user-name {
  margin: 0 0 4px 0;
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}

.user-nickname {
  margin: 0;
  font-size: 13px;
  color: #909399;
}

.card-meta {
  margin-bottom: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid #f0f2f5;
}

.meta-item {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
  font-size: 13px;
}

.meta-label {
  color: #909399;
}

.meta-value {
  color: #606266;
  font-weight: 500;
}

.card-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.card-actions .el-button {
  flex: 1;
  min-width: 0;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .desktop-table {
    display: none !important;
  }
  
  .mobile-card-list {
    display: block;
  }
  
  .header-content {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .action-section {
    margin-top: 15px;
    width: 100%;
  }
  
  .search-card .el-col {
    margin-bottom: 10px;
    width: 100% !important;
  }
  
  .search-card .el-row {
    flex-direction: column;
  }
  
  .pagination-container {
    text-align: center;
  }
  
  .pagination-container ::v-deep .el-pagination {
    justify-content: center;
    flex-wrap: wrap;
  }
}

@media (max-width: 480px) {
  .user-management {
    padding: 0;
  }
  
  .header-card,
  .search-card,
  .table-card {
    margin: 10px;
    margin-bottom: 10px;
  }
  
  .card-actions .el-button {
    font-size: 12px;
    padding: 5px 8px;
  }
  
  .dialog-footer {
    text-align: center;
  }
  
  .permission-dialog ::v-deep .el-dialog {
    width: 95% !important;
    margin: 0 auto;
  }
}

/* 权限对话框样式 */
.permission-header {
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 1px solid #e4e7ed;
}

.permission-header strong {
  color: #409EFF;
}

.selected-count {
  margin-left: auto;
  color: #67c23a;
  font-weight: 600;
}

.permission-content {
  margin-top: 20px;
}

.permission-tip {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 15px;
  background: #ecf5ff;
  border-radius: 4px;
  color: #409EFF;
  font-size: 14px;
  margin-bottom: 15px;
}

.permission-tip i {
  font-size: 16px;
}

.permission-search {
  margin-bottom: 15px;
}

.permission-pagination {
  margin-top: 15px;
  display: flex;
  justify-content: center;
}

.permission-content ::v-deep .el-table th {
  background: #f5f7f9;
}

.permission-content ::v-deep .el-table {
  font-size: 14px;
}
</style>

